"use client"

import * as React from "react"

import { createPersistedStore, usePersistedStore } from "@/lib/persisted-store"

import { useExamRole, useIsProgramDirector } from "@/contexts/exam-role-context"
import { useExamCategories } from "@/hooks/use-exam-categories"
import { useLeoWorkspaceSurface } from "@/hooks/use-leo-workspace-surface"
import {
  appliesToProgram,
  categoriesInDimension,
  DEFAULT_CATEGORY_DIMENSIONS,
  isDimensionRestricted,
  contentDimensions,
  courseTreesFor,
  type CategoryDimension,
  type ExamCategory,
} from "@/lib/exam-categories"

const DEMO_PROGRAM = "Nursing"

export const CATEGORY_DIMENSIONS_STORE_KEY = "shell:exam-category-dimensions:v1"

/**
 * The standards a question is classified along, as the admin configured them.
 *
 * One store, read by the question editor (which tags with them) and by the
 * settings screen (which defines them). Managing a standard and using it in a
 * question are different jobs for different people, but they must never see
 * different standards.
 */
const dimensionsStore = createPersistedStore<CategoryDimension[]>(CATEGORY_DIMENSIONS_STORE_KEY, () =>
  DEFAULT_CATEGORY_DIMENSIONS.map((dimension) => ({ ...dimension })),
)

export function useCategoryDimensions() {
  const [stored, setDimensions] = usePersistedStore(dimensionsStore)

  // A standard added to the defaults after this browser saved its own list
  // still appears. Saved edits win; only missing ones are added.
  const dimensions = React.useMemo(() => {
    const have = new Set(stored.map((dimension) => dimension.id))
    const missing = DEFAULT_CATEGORY_DIMENSIONS.filter((dimension) => !have.has(dimension.id))
    // A list saved before standards knew their program gets the default's.
    const programsOf = new Map(DEFAULT_CATEGORY_DIMENSIONS.map((d) => [d.id, d.programs]))
    const filled = stored.map((dimension) =>
      dimension.programs || !programsOf.get(dimension.id)
        ? dimension
        : { ...dimension, programs: programsOf.get(dimension.id) },
    )
    return missing.length > 0 ? [...filled, ...missing.map((d) => ({ ...d }))] : filled
  }, [stored])

  const updateDimension = React.useCallback(
    (id: string, patch: Partial<Omit<CategoryDimension, "id">>) => {
      setDimensions(() =>
        dimensions.map((dimension) =>
          dimension.id === id ? { ...dimension, ...patch } : dimension,
        ),
      )
    },
    [dimensions, setDimensions],
  )

  const addDimension = React.useCallback(
    /** `extra.id` brings back one removed earlier (a type switched back to a hierarchy). */
    (name: string, extra: Partial<Omit<CategoryDimension, "name">> = {}): CategoryDimension => {
      const base = name
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-|-$/g, "")
      const created: CategoryDimension = {
        id: `standard-${base || "new"}-${Date.now().toString(36)}`,
        name: name.trim(),
        tier: "program",
        purpose: "reporting",
        required: false,
        ...extra,
      }
      setDimensions(() => [...dimensions, created])
      return created
    },
    [dimensions, setDimensions],
  )

  /** A tree a course makes for itself: outcomes, a lecture index, an ad hoc grouping. */
  const addCourseTree = React.useCallback(
    (courseId: string, name: string): CategoryDimension => {
      const base = name
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-|-$/g, "")
      const created: CategoryDimension = {
        id: `course-tree-${courseId}-${base || "new"}-${Date.now().toString(36)}`,
        name: name.trim(),
        kind: "course_tree",
        tier: "course",
        purpose: "coverage",
        required: false,
        ownerCourseId: courseId,
      }
      setDimensions(() => [...dimensions, created])
      return created
    },
    [dimensions, setDimensions],
  )

  const removeDimension = React.useCallback(
    (id: string) => setDimensions(() => dimensions.filter((row) => row.id !== id)),
    [dimensions, setDimensions],
  )

  return { dimensions, updateDimension, addDimension, addCourseTree, removeDimension }
}

/**
 * The standards a question itself is tagged with.
 *
 * Only the course-level standard: the one faculty and the program director
 * both use, and the only one a question carries. Program-level standards
 * (NCLEX client needs, program competencies) are defined and reported on by
 * the director in Settings; showing them in every faculty member's editor
 * asked for a category they have no reason to know.
 */
/**
 * The program standards this person may tag a question with: every program
 * standard that is not restricted to the director, or all of them for the
 * director. Which ones are open is each standard's own setting.
 */
export function useTaggableStandards() {
  const { dimensions } = useCategoryDimensions()
  const isDirector = useIsProgramDirector()
  // The workspace program. This build has no scope switcher on the question
  // routes, so it falls back to the program its courses and bank belong to.
  const program = useLeoWorkspaceSurface().program ?? DEMO_PROGRAM
  return React.useMemo(
    () =>
      dimensions.filter(
        (dimension) =>
          dimension.tier === "program" &&
          appliesToProgram(dimension, program) &&
          !isDimensionRestricted(dimension, isDirector),
      ),
    [dimensions, isDirector, program],
  )
}

/**
 * The program standards this person can read on a question but not tag with:
 * the tree stays visible, labelled with who sets it, instead of disappearing.
 */
export function useViewOnlyStandards() {
  const { dimensions } = useCategoryDimensions()
  const isDirector = useIsProgramDirector()
  const program = useLeoWorkspaceSurface().program ?? DEMO_PROGRAM
  return React.useMemo(
    () =>
      dimensions.filter(
        (dimension) =>
          dimension.tier === "program" &&
          appliesToProgram(dimension, program) &&
          isDimensionRestricted(dimension, isDirector),
      ),
    [dimensions, isDirector, program],
  )
}

/** The course content tree of the program in view. Each program has its own. */
export function useQuestionDimensions() {
  const { dimensions } = useCategoryDimensions()
  const program = useLeoWorkspaceSurface().program ?? DEMO_PROGRAM
  return React.useMemo(
    () => contentDimensions(dimensions).filter((dimension) => appliesToProgram(dimension, program)),
    [dimensions, program],
  )
}

/** The trees the given course owns. */
export function useCourseTrees(courseId: string | undefined) {
  const { dimensions } = useCategoryDimensions()
  return React.useMemo(() => courseTreesFor(dimensions, courseId), [courseId, dimensions])
}

/**
 * May this person shape the course's own trees? The program director always;
 * a faculty member for a course they coordinate.
 */
export function useCanManageCourseTrees(course: { instructor?: string } | undefined): boolean {
  const { role } = useExamRole()
  if (role === "Admin") return true
  return role === "Faculty" && course?.instructor === "Dr. Alan Turing"
}

/**
 * The categories Leo may suggest from: the same ones the category navigation
 * offers. Course content (narrowed to the question's course when given) plus
 * the program standards this person may tag. Never another program's standards,
 * never a standard the role cannot tag.
 */
export function useLeoCategoryCatalog(contentOptions?: readonly ExamCategory[]): ExamCategory[] {
  const { categories } = useExamCategories()
  const content = useQuestionDimensions()[0]
  const standards = useTaggableStandards()
  return React.useMemo(() => {
    const own = contentOptions ?? (content ? categoriesInDimension(categories, content.id) : [])
    const fromStandards = standards.flatMap((dimension) => categoriesInDimension(categories, dimension.id))
    return [...own, ...fromStandards]
  }, [categories, content, contentOptions, standards])
}
